import React, { useState, useMemo } from 'react';
import axios from '../AxiosInstance';

const ClipDemo = () => {
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [topK, setTopK] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resultsV1, setResultsV1] = useState(null);
  const [resultsV2, setResultsV2] = useState(null);
  const [timings, setTimings] = useState({ v1: null, v2: null });

  const canSearch = useMemo(() => !!imageDataUrl && !loading, [imageDataUrl, loading]);

  const onFileChange = async (e) => {
    setError('');
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setImageDataUrl(typeof dataUrl === 'string' ? dataUrl : '');
    };
    reader.onerror = () => setError('Failed to read file');
    reader.readAsDataURL(file);
  };

  const runSearch = async () => {
    if (!imageDataUrl) return;
    setLoading(true);
    setError('');
    setResultsV1(null);
    setResultsV2(null);
    setTimings({ v1: null, v2: null });

    const payload = { image: imageDataUrl, topK: Number(topK) || 5 };

    // Measure each request independently so faster request shows smaller elapsed
    const t1 = performance.now();
    let v1Data = null; let v1Err = null; let v1Ms = null;
    const pV1 = axios.post('/optimized-clip/optimized-search', payload)
      .then(r => { v1Data = r.data; v1Ms = performance.now() - t1; })
      .catch(e => { v1Err = e; v1Ms = performance.now() - t1; });

    const t2 = performance.now();
    let v2Data = null; let v2Err = null; let v2Ms = null;
    const pV2 = axios.post('/clipv2/search', payload)
      .then(r => { v2Data = r.data; v2Ms = performance.now() - t2; })
      .catch(e => { v2Err = e; v2Ms = performance.now() - t2; });

    try {
      await Promise.all([pV1, pV2]);

      setResultsV1(v1Err ? { success: false, error: v1Err?.response?.data?.error || v1Err.message || 'V1 failed' } : v1Data);
      setResultsV2(v2Err ? { success: false, error: v2Err?.response?.data?.error || v2Err.message || 'V2 failed' } : v2Data);

      setTimings({ v1: Math.max(0, v1Ms ?? 0), v2: Math.max(0, v2Ms ?? 0) });
    } catch (e) {
      setError(e.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h2>CLIP Search A/B Demo</h2>
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginBottom: 16 }}>
        <input type="file" accept="image/*" onChange={onFileChange} />
        <label>
          Top K:
          <input
            type="number"
            min={1}
            max={20}
            value={topK}
            onChange={(e) => setTopK(e.target.value)}
            style={{ width: 80, marginLeft: 8 }}
          />
        </label>
        <button onClick={runSearch} disabled={!canSearch}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {imageDataUrl && (
        <div style={{ marginBottom: 16 }}>
          <img src={imageDataUrl} alt="preview" style={{ maxWidth: 240, borderRadius: 8, border: '1px solid #ddd' }} />
        </div>
      )}

      {error && (
        <div style={{ color: 'red', marginBottom: 16 }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div>
          <h3>Current (optimized-clip)</h3>
          <div style={{ color: '#666', marginBottom: 8 }}>Elapsed: {timings.v1 !== null ? `${timings.v1.toFixed(0)} ms` : '-'}</div>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12, borderRadius: 8, border: '1px solid #eee', maxHeight: 400, overflow: 'auto' }}>
            {resultsV1 ? JSON.stringify(resultsV1, null, 2) : 'Awaiting results...'}
          </pre>
        </div>
        <div>
          <h3>CLIPv2 (faiss)</h3>
          <div style={{ color: '#666', marginBottom: 8 }}>Elapsed: {timings.v2 !== null ? `${timings.v2.toFixed(0)} ms` : '-'}</div>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 12, borderRadius: 8, border: '1px solid #eee', maxHeight: 400, overflow: 'auto' }}>
            {resultsV2 ? JSON.stringify(resultsV2, null, 2) : 'Awaiting results...'}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default ClipDemo;
